import { z } from 'zod';

import {
  TraitDeclarationSchema,
  TypeDefinitionSchema,
  type TraitDeclaration,
  type TypeDefinition,
} from './pack.js';
import {
  PredicateDeclarationSchema,
  RequiredLocalizedNameSchema,
  type LocalizedString,
  type PredicateDeclaration,
} from './predicate.js';
import {
  DimensionDefinitionSchema,
  UnitDefinitionSchema,
  type DimensionDefinition,
  type UnitDefinition,
} from './units.js';

/**
 * Pack-definition meta-ops (#73) — the substrate layer of the trait editor.
 *
 * Per ADR-0001, GM-authored traits and predicate fields are stored as
 * append-only op-log meta-ops describing a **campaign-local homebrew
 * Pack**. The same machinery the entity meta-ops use (`entity-ops.ts`,
 * chained under a reserved `__`-prefixed predicate slot) applies here:
 * the op log is the source of truth, the registry/trait-index is a
 * rebuildable view folded from these ops on top of the bundled packs.
 *
 *   - `pack.define_predicate` carries a full `PredicateDeclaration`.
 *     Create AND update are the same op — last-writer-wins per id, so
 *     renaming a label or widening a value-kind is just another define.
 *   - `pack.define_trait` carries a `TraitDeclaration` (id, applies_to,
 *     predicates).
 *   - `pack.retire_predicate` / `pack.retire_trait` remove a definition
 *     by id (the "delete" of CRUD). Retiring never rewrites history; the
 *     fold simply stops surfacing the id. Facts already written against a
 *     retired predicate persist in the log untouched (mirrors the
 *     detach-trait contract in `entity-ops.ts`).
 *
 * Replay ordering invariant (ADR-0001 Consequences): a `pack.define_*`
 * op must fold before the fact ops that reference the predicate, or the
 * fact lands against an unknown predicate. The substrate appends
 * definitions before use; replay folds the pack layer before the entity
 * layer.
 */

/**
 * Predicate slot reserved for pack-definition meta-ops. Like
 * `ENTITY_META_PREDICATE` (`__entity`), the `__`-prefix keeps these ops
 * out of any fact chain and out of the user-declarable namespace.
 */
export const PACK_META_PREDICATE = '__pack';

/**
 * The fixed subject id for a Campaign's homebrew Pack. Every
 * `pack.define_*` op in a Campaign chains under
 * `(HOMEBREW_PACK_ID, __pack)`. The id is stable so the homebrew Pack is
 * addressable for export / promotion to a shared Pack (ADR-0001).
 */
export const HOMEBREW_PACK_ID = 'homebrew';

/**
 * Reject `__`-prefixed ids in define payloads — that namespace is
 * engine-reserved (meta-op predicate slots). Shared by both define
 * schemas.
 */
const reservedIdRefinement = (id: string): boolean => !id.startsWith('__');
const RESERVED_ID_MESSAGE = 'ids beginning with "__" are reserved by the engine';

export const PackDefinePredicatePayloadSchema = z
  .object({
    kind: z.literal('pack.define_predicate'),
    declaration: PredicateDeclarationSchema.refine(
      (d) => reservedIdRefinement(d.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefinePredicatePayload = z.infer<typeof PackDefinePredicatePayloadSchema>;

export const PackDefineTraitPayloadSchema = z
  .object({
    kind: z.literal('pack.define_trait'),
    declaration: TraitDeclarationSchema.refine(
      (d) => reservedIdRefinement(d.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefineTraitPayload = z.infer<typeof PackDefineTraitPayloadSchema>;

export const PackRetirePredicatePayloadSchema = z
  .object({
    kind: z.literal('pack.retire_predicate'),
    predicate_id: z.string().min(1),
  })
  .strict();
export type PackRetirePredicatePayload = z.infer<typeof PackRetirePredicatePayloadSchema>;

export const PackRetireTraitPayloadSchema = z
  .object({
    kind: z.literal('pack.retire_trait'),
    trait_id: z.string().min(1),
  })
  .strict();
export type PackRetireTraitPayload = z.infer<typeof PackRetireTraitPayloadSchema>;

// ─── Campaign-local entity types (#schema-settings / 03a-entities.md §4) ──
//
// A type is a `(root, traits[])` quick-create template. It rides the same
// `__pack` meta-op chain as predicates/traits (ADR-0001): define = create OR
// update (last-writer-wins per id), retire = the "delete" of CRUD. Retiring
// never rewrites history — entities already created from a type keep the
// traits that were attached at create time (a type is a template, not a live
// link), so retiring just drops the template from the quick-create list.

export const PackDefineTypePayloadSchema = z
  .object({
    kind: z.literal('pack.define_type'),
    declaration: TypeDefinitionSchema.refine(
      (d) => reservedIdRefinement(d.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefineTypePayload = z.infer<typeof PackDefineTypePayloadSchema>;

export const PackRetireTypePayloadSchema = z
  .object({
    kind: z.literal('pack.retire_type'),
    type_id: z.string().min(1),
  })
  .strict();
export type PackRetireTypePayload = z.infer<typeof PackRetireTypePayloadSchema>;

// ─── Campaign-local unit definitions (ADR-0002) ────────────────────
//
// Units ride the same `__pack` meta-op chain as predicates/traits
// (ADR-0001): a campaign declares its own dimensions + units in the op
// log, the fold builds an overlay, and the client merges that overlay
// with the common unit registry to drive the number-trait unit picker.
// Create AND update are the same op (last-writer-wins per id); retire
// tombstones an id without rewriting history.

export const PackDefineDimensionPayloadSchema = z
  .object({
    kind: z.literal('pack.define_dimension'),
    definition: DimensionDefinitionSchema.refine(
      (d) => reservedIdRefinement(d.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefineDimensionPayload = z.infer<typeof PackDefineDimensionPayloadSchema>;

export const PackDefineUnitPayloadSchema = z
  .object({
    kind: z.literal('pack.define_unit'),
    definition: UnitDefinitionSchema.refine(
      (u) => reservedIdRefinement(u.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefineUnitPayload = z.infer<typeof PackDefineUnitPayloadSchema>;

export const PackRetireDimensionPayloadSchema = z
  .object({
    kind: z.literal('pack.retire_dimension'),
    dimension_id: z.string().min(1),
  })
  .strict();
export type PackRetireDimensionPayload = z.infer<typeof PackRetireDimensionPayloadSchema>;

export const PackRetireUnitPayloadSchema = z
  .object({
    kind: z.literal('pack.retire_unit'),
    unit_id: z.string().min(1),
  })
  .strict();
export type PackRetireUnitPayload = z.infer<typeof PackRetireUnitPayloadSchema>;

// ─── Campaign-local calendar definitions (#177, ADR-0004 §1/§4) ─────
//
// A calendar is a pack-extractable DEFINITION (the #3 bucket of ADR-0004
// §4), NOT a lore entity and NOT a setting: identity is an opaque,
// minted `cal_` id, naming is a mandatory localized name, and the
// structure (months/eras/leap/anchor) is reusable schema folded into the
// registry and carried into extracted packs. The campaign's
// `canonical_calendar` *choice* is a separate per-campaign pointer
// setting (config-ops, #180) holding the chosen definition's id.
//
// Calendars ride the same `__pack` meta-op chain as predicates/traits
// (ADR-0001): define = create OR update (last-writer-wins per id), retire
// tombstones an id without rewriting history.
//
// CIRCULAR-DEP NOTE: the authoritative structural calendar schema
// (`CalendarSchema`) lives in `@dl/calendar`, which depends on
// `@dl/shared` — so `@dl/shared` cannot import it (it would form a
// cycle). Mirroring the existing `PackSchema.calendars: z.unknown()`
// convention, the meta-op here validates only what `@dl/shared` owns —
// the opaque `cal_` id, the reserved-id refinement, and the mandatory
// name — and carries the structural body opaquely. `@dl/calendar`
// re-validates a folded definition through `CalendarDefinitionSchema`
// (its own chokepoint) before the conversion engine consumes it.

/**
 * The minimal calendar-definition shape `@dl/shared` validates + folds.
 * The opaque `cal_` `id` and the mandatory localized `name` are the
 * definition's identity + display; everything else (cycles/eras/leap/
 * anchor) is carried as a passthrough body parsed by `@dl/calendar`. This
 * mirrors `PackSchema.calendars` being typed `z.unknown()` to avoid the
 * `@dl/shared` ↔ `@dl/calendar` cycle.
 */
export interface CalendarDefinitionLike {
  id: string;
  name: LocalizedString;
  [key: string]: unknown;
}

/**
 * Loose envelope validator for a calendar definition carried by a
 * `pack.define_calendar` op. Enforces only the `@dl/shared`-owned
 * contract: an opaque `cal_` id (not engine-reserved) and a mandatory
 * default-locale name. The structural body passes through untouched
 * (validated by `@dl/calendar`'s `CalendarDefinitionSchema`).
 */
export const CalendarDefinitionEnvelopeSchema = z
  .object({
    id: z.string().min(1),
    name: RequiredLocalizedNameSchema,
  })
  .passthrough();

export const PackDefineCalendarPayloadSchema = z
  .object({
    kind: z.literal('pack.define_calendar'),
    definition: CalendarDefinitionEnvelopeSchema.refine(
      (d) => reservedIdRefinement(d.id),
      RESERVED_ID_MESSAGE,
    ),
  })
  .strict();
export type PackDefineCalendarPayload = z.infer<typeof PackDefineCalendarPayloadSchema>;

export const PackRetireCalendarPayloadSchema = z
  .object({
    kind: z.literal('pack.retire_calendar'),
    calendar_id: z.string().min(1),
  })
  .strict();
export type PackRetireCalendarPayload = z.infer<typeof PackRetireCalendarPayloadSchema>;

// ─── Deliberate cross-vocabulary unification (#178, ADR-0004 §8) ────
//
// Opaque ids give safe NON-unification by default (two packs' "Population"
// stay distinct ids). Deliberate unification — "this homebrew predicate IS
// core's population" — is an explicit `pack.alias_vocab { from_id, to_id }`
// meta-op that re-points `from_id` at `to_id` at READ / PROJECTION time.
// This is the intentional replacement for the removed accidental
// token-merging (the former `previous_names`-as-identity path, dropped in
// #172/#175); the op log is never rewritten — facts keep their original
// `from_id`, the alias only re-homes the projection key so the two chains
// merge under the target.
//
// Like the define/retire ops it rides the same `__pack` meta-op chain
// (ADR-0001): last-writer-wins per `from_id`. Both ends are reserved-id
// refined (an alias can never source from or target an engine sentinel).
// Cycles and dangling targets are surfaced as NON-blocking advisories at
// fold time (never a throw — see `foldPackOps`).

export const PackAliasVocabPayloadSchema = z
  .object({
    kind: z.literal('pack.alias_vocab'),
    from_id: z.string().min(1).refine(reservedIdRefinement, RESERVED_ID_MESSAGE),
    to_id: z.string().min(1).refine(reservedIdRefinement, RESERVED_ID_MESSAGE),
  })
  .strict();
export type PackAliasVocabPayload = z.infer<typeof PackAliasVocabPayloadSchema>;

export const PackOpPayloadSchema = z.union([
  PackDefinePredicatePayloadSchema,
  PackDefineTraitPayloadSchema,
  PackRetirePredicatePayloadSchema,
  PackRetireTraitPayloadSchema,
  PackDefineTypePayloadSchema,
  PackRetireTypePayloadSchema,
  PackDefineDimensionPayloadSchema,
  PackDefineUnitPayloadSchema,
  PackRetireDimensionPayloadSchema,
  PackRetireUnitPayloadSchema,
  PackDefineCalendarPayloadSchema,
  PackRetireCalendarPayloadSchema,
  PackAliasVocabPayloadSchema,
]);
export type PackOpPayload = z.infer<typeof PackOpPayloadSchema>;

// ─── Builders ──────────────────────────────────────────────────────

/** Build a define-predicate payload (create or update — last writer wins). */
export function packDefinePredicate(
  declaration: PredicateDeclaration,
): PackDefinePredicatePayload {
  return { kind: 'pack.define_predicate', declaration };
}

/** Build a define-trait payload (create or update — last writer wins). */
export function packDefineTrait(declaration: TraitDeclaration): PackDefineTraitPayload {
  return { kind: 'pack.define_trait', declaration };
}

/** Build a retire-predicate payload (the "delete" of CRUD). */
export function packRetirePredicate(predicateId: string): PackRetirePredicatePayload {
  return { kind: 'pack.retire_predicate', predicate_id: predicateId };
}

/** Build a retire-trait payload. */
export function packRetireTrait(traitId: string): PackRetireTraitPayload {
  return { kind: 'pack.retire_trait', trait_id: traitId };
}

/** Build a define-type payload (create or update — last writer wins). */
export function packDefineType(declaration: TypeDefinition): PackDefineTypePayload {
  return { kind: 'pack.define_type', declaration };
}

/** Build a retire-type payload (the "delete" of CRUD). */
export function packRetireType(typeId: string): PackRetireTypePayload {
  return { kind: 'pack.retire_type', type_id: typeId };
}

/** Build a define-dimension payload (create or update — last writer wins). */
export function packDefineDimension(
  definition: DimensionDefinition,
): PackDefineDimensionPayload {
  return { kind: 'pack.define_dimension', definition };
}

/** Build a define-unit payload (create or update — last writer wins). */
export function packDefineUnit(definition: UnitDefinition): PackDefineUnitPayload {
  return { kind: 'pack.define_unit', definition };
}

/** Build a retire-dimension payload (the "delete" of CRUD). */
export function packRetireDimension(dimensionId: string): PackRetireDimensionPayload {
  return { kind: 'pack.retire_dimension', dimension_id: dimensionId };
}

/** Build a retire-unit payload. */
export function packRetireUnit(unitId: string): PackRetireUnitPayload {
  return { kind: 'pack.retire_unit', unit_id: unitId };
}

/** Build a define-calendar payload (create or update — last writer wins). */
export function packDefineCalendar(
  definition: CalendarDefinitionLike,
): PackDefineCalendarPayload {
  return { kind: 'pack.define_calendar', definition };
}

/** Build a retire-calendar payload (the "delete" of CRUD). */
export function packRetireCalendar(calendarId: string): PackRetireCalendarPayload {
  return { kind: 'pack.retire_calendar', calendar_id: calendarId };
}

/**
 * Build an alias/merge payload (#178): re-point `fromId` at `toId` at
 * read/projection time. Last-writer-wins per `fromId`.
 */
export function packAliasVocab(fromId: string, toId: string): PackAliasVocabPayload {
  return { kind: 'pack.alias_vocab', from_id: fromId, to_id: toId };
}

// ─── Type guards ───────────────────────────────────────────────────

function hasKind(payload: unknown, kind: string): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === kind
  );
}

export function isPackDefinePredicate(payload: unknown): payload is PackDefinePredicatePayload {
  return hasKind(payload, 'pack.define_predicate');
}
export function isPackDefineTrait(payload: unknown): payload is PackDefineTraitPayload {
  return hasKind(payload, 'pack.define_trait');
}
export function isPackRetirePredicate(payload: unknown): payload is PackRetirePredicatePayload {
  return hasKind(payload, 'pack.retire_predicate');
}
export function isPackRetireTrait(payload: unknown): payload is PackRetireTraitPayload {
  return hasKind(payload, 'pack.retire_trait');
}
export function isPackDefineType(payload: unknown): payload is PackDefineTypePayload {
  return hasKind(payload, 'pack.define_type');
}
export function isPackRetireType(payload: unknown): payload is PackRetireTypePayload {
  return hasKind(payload, 'pack.retire_type');
}
export function isPackDefineDimension(payload: unknown): payload is PackDefineDimensionPayload {
  return hasKind(payload, 'pack.define_dimension');
}
export function isPackDefineUnit(payload: unknown): payload is PackDefineUnitPayload {
  return hasKind(payload, 'pack.define_unit');
}
export function isPackRetireDimension(payload: unknown): payload is PackRetireDimensionPayload {
  return hasKind(payload, 'pack.retire_dimension');
}
export function isPackRetireUnit(payload: unknown): payload is PackRetireUnitPayload {
  return hasKind(payload, 'pack.retire_unit');
}
export function isPackDefineCalendar(payload: unknown): payload is PackDefineCalendarPayload {
  return hasKind(payload, 'pack.define_calendar');
}
export function isPackRetireCalendar(payload: unknown): payload is PackRetireCalendarPayload {
  return hasKind(payload, 'pack.retire_calendar');
}
export function isPackAliasVocab(payload: unknown): payload is PackAliasVocabPayload {
  return hasKind(payload, 'pack.alias_vocab');
}

// ─── Fold ──────────────────────────────────────────────────────────

/**
 * The campaign-local homebrew Pack's definitions, folded from the op
 * log. Returned as id-sorted arrays for deterministic consumption.
 */
export interface HomebrewPackDefs {
  predicates: PredicateDeclaration[];
  traits: TraitDeclaration[];
  /** Campaign-local entity types (quick-create templates), id-sorted. */
  types: TypeDefinition[];
  /** Campaign-local unit dimensions, id-sorted (ADR-0002). */
  dimensions: DimensionDefinition[];
  /** Campaign-local units, id-sorted (ADR-0002). */
  units: UnitDefinition[];
  /**
   * Campaign-local calendar definitions, id-sorted (#177, ADR-0004
   * §1/§4). Carried as the loose `@dl/shared` envelope shape; consumers
   * that drive the conversion engine re-validate through
   * `@dl/calendar`'s `CalendarDefinitionSchema`.
   */
  calendars: CalendarDefinitionLike[];
  /** Retired dimension ids (tombstones). */
  retiredDimensions: Record<string, true>;
  /** Retired unit ids (tombstones). */
  retiredUnits: Record<string, true>;
  /** Retired calendar ids (tombstones). */
  retiredCalendars: Record<string, true>;
  /**
   * Deliberate cross-vocabulary aliases (#178, ADR-0004 §8): a
   * `from_id → to_id` map folded from `pack.alias_vocab` ops. Distinct from
   * the removed automatic `previous_names` mechanism — this is opt-in,
   * explicit GM/pack intent. Self-aliases and cycles are *not* folded into
   * this map (they are surfaced via {@link aliasAdvisories} instead), so the
   * map is always safe to walk with {@link resolveVocabAlias}. Retiring a
   * `from_id` drops its alias (retire wins over alias for the same source).
   */
  aliases: Record<string, string>;
  /**
   * Non-blocking advisories raised while folding aliases (#178): cycles
   * (incl. self-alias) and dangling targets (a `to_id` with no live
   * definition in this fold). Never thrown — the fold always returns a
   * usable map; consumers surface these through the auditor / advisory UI.
   */
  aliasAdvisories: AliasAdvisory[];
}

/**
 * A non-blocking advisory about an alias edge (#178). `cycle` lists the ids
 * forming the loop (a self-alias is `[id]`); `dangling` lists the missing
 * target id(s). Content-addressable shape so the auditor can dedupe.
 */
export interface AliasAdvisory {
  kind: 'cycle' | 'dangling';
  ids: string[];
}

/**
 * Resolve a vocab id through an alias map to its terminal target. Walks the
 * `from_id → to_id` chain (a well-formed map from {@link foldPackOps} has no
 * cycles, but a defensive visited-set still guarantees termination if a
 * caller supplies a hand-built cyclic map — it returns the entry id rather
 * than looping). An id with no alias resolves to itself.
 */
export function resolveVocabAlias(aliases: Record<string, string>, id: string): string {
  let current = id;
  const seen = new Set<string>([current]);
  for (;;) {
    const next = aliases[current];
    if (next === undefined) return current;
    if (seen.has(next)) return id; // cycle guard — return the entry id
    seen.add(next);
    current = next;
  }
}

interface PackOpLike {
  predicate: string;
  payload: unknown;
}

/**
 * Fold an HLC-ordered op stream into the homebrew Pack's current
 * predicate + trait definitions. Only ops on the `__pack` slot are
 * considered; everything else is ignored so this can run over the full
 * op log. Last-writer-wins per id; retire removes. Unknown payload kinds
 * are skipped (forward-compatible).
 *
 * The caller delivers ops in HLC order; the fold is order-sensitive and
 * does not re-sort.
 */
export function foldPackOps(ops: readonly PackOpLike[]): HomebrewPackDefs {
  const predicates = new Map<string, PredicateDeclaration>();
  const traits = new Map<string, TraitDeclaration>();
  const types = new Map<string, TypeDefinition>();
  const dimensions = new Map<string, DimensionDefinition>();
  const units = new Map<string, UnitDefinition>();
  const calendars = new Map<string, CalendarDefinitionLike>();
  const retiredDimensions: Record<string, true> = {};
  const retiredUnits: Record<string, true> = {};
  const retiredCalendars: Record<string, true> = {};
  // Raw alias edges (from_id → to_id), last-writer-wins per from_id. Cycle /
  // dangling resolution + retire-precedence runs in a second pass below, so
  // the fold never throws on a malformed edge.
  const rawAliases = new Map<string, string>();
  // Every vocab id that has a LIVE definition at the end of the fold — used
  // to (a) drop an alias whose source was retired and (b) flag a dangling
  // target. Retire removes; re-define re-adds.
  const definedIds = new Set<string>();
  // Retire wins over alias for the same SOURCE, by op order: retiring an id
  // drops any alias edge currently sourced from it (a later re-alias of the
  // same id reinstates it). A retire of an alias TARGET leaves the edge in
  // place — it becomes a dangling-target advisory below.
  const dropDefined = (id: string): void => {
    definedIds.delete(id);
    rawAliases.delete(id);
  };

  for (const op of ops) {
    if (op.predicate !== PACK_META_PREDICATE) continue;
    const payload = op.payload;
    if (isPackDefinePredicate(payload)) {
      predicates.set(payload.declaration.id, payload.declaration);
      definedIds.add(payload.declaration.id);
    } else if (isPackDefineTrait(payload)) {
      traits.set(payload.declaration.id, payload.declaration);
      definedIds.add(payload.declaration.id);
    } else if (isPackRetirePredicate(payload)) {
      predicates.delete(payload.predicate_id);
      dropDefined(payload.predicate_id);
    } else if (isPackRetireTrait(payload)) {
      traits.delete(payload.trait_id);
      dropDefined(payload.trait_id);
    } else if (isPackDefineType(payload)) {
      types.set(payload.declaration.id, payload.declaration);
      definedIds.add(payload.declaration.id);
    } else if (isPackRetireType(payload)) {
      types.delete(payload.type_id);
      dropDefined(payload.type_id);
    } else if (isPackDefineDimension(payload)) {
      dimensions.set(payload.definition.id, payload.definition);
      definedIds.add(payload.definition.id);
      // Re-defining after retire clears the tombstone.
      delete retiredDimensions[payload.definition.id];
    } else if (isPackDefineUnit(payload)) {
      units.set(payload.definition.id, payload.definition);
      definedIds.add(payload.definition.id);
      delete retiredUnits[payload.definition.id];
    } else if (isPackRetireDimension(payload)) {
      dimensions.delete(payload.dimension_id);
      dropDefined(payload.dimension_id);
      retiredDimensions[payload.dimension_id] = true;
    } else if (isPackRetireUnit(payload)) {
      units.delete(payload.unit_id);
      dropDefined(payload.unit_id);
      retiredUnits[payload.unit_id] = true;
    } else if (isPackDefineCalendar(payload)) {
      calendars.set(payload.definition.id, payload.definition);
      definedIds.add(payload.definition.id);
      delete retiredCalendars[payload.definition.id];
    } else if (isPackRetireCalendar(payload)) {
      calendars.delete(payload.calendar_id);
      dropDefined(payload.calendar_id);
      retiredCalendars[payload.calendar_id] = true;
    } else if (isPackAliasVocab(payload)) {
      // Last-writer-wins per from_id. A re-alias after a retire of the same
      // source reinstates the edge (the retire's `dropDefined` cleared it,
      // this op re-adds it) — alias-vs-retire precedence is op-ordered.
      rawAliases.set(payload.from_id, payload.to_id);
    }
  }

  const { aliases, aliasAdvisories } = resolveAliasEdges(rawAliases, definedIds);

  const byId = (a: { id: string }, b: { id: string }): number => a.id.localeCompare(b.id);
  return {
    predicates: [...predicates.values()].sort(byId),
    traits: [...traits.values()].sort(byId),
    types: [...types.values()].sort(byId),
    dimensions: [...dimensions.values()].sort(byId),
    units: [...units.values()].sort(byId),
    calendars: [...calendars.values()].sort(byId),
    retiredDimensions,
    retiredUnits,
    retiredCalendars,
    aliases,
    aliasAdvisories,
  };
}

/**
 * Resolve raw alias edges (#178) into a safe `from_id → to_id` map plus
 * non-blocking advisories. Self-aliases and edges participating in a cycle
 * are dropped (never folded) and surfaced as `cycle` advisories; an edge
 * whose target has no live definition in this fold is kept *out* of nothing
 * but flagged as a `dangling` advisory (the re-point still happens — a
 * dangling target just means the merged chain has no schema). The op log is
 * never rewritten; this is a rebuildable read-time view.
 */
function resolveAliasEdges(
  rawAliases: ReadonlyMap<string, string>,
  definedIds: ReadonlySet<string>,
): { aliases: Record<string, string>; aliasAdvisories: AliasAdvisory[] } {
  const advisories: AliasAdvisory[] = [];
  // Self-alias is a degenerate 1-cycle.
  const selfAliases: string[] = [];
  const candidate = new Map<string, string>();
  for (const [from, to] of rawAliases) {
    if (from === to) {
      selfAliases.push(from);
      continue;
    }
    candidate.set(from, to);
  }
  for (const id of selfAliases) advisories.push({ kind: 'cycle', ids: [id] });

  // Detect cycles among the remaining edges: follow each source until we hit
  // a terminal (no outgoing edge) or revisit a node. Any edge on a detected
  // cycle is removed from the safe map.
  const cyclic = new Set<string>();
  for (const start of candidate.keys()) {
    if (cyclic.has(start)) continue;
    const path: string[] = [];
    const onPath = new Set<string>();
    let node: string | undefined = start;
    while (node !== undefined && !cyclic.has(node)) {
      if (onPath.has(node)) {
        // Found a loop; everything from `node` onward is part of the cycle.
        const loopStart = path.indexOf(node);
        const loop = path.slice(loopStart);
        for (const n of loop) cyclic.add(n);
        advisories.push({ kind: 'cycle', ids: [...loop] });
        break;
      }
      path.push(node);
      onPath.add(node);
      node = candidate.get(node);
    }
  }

  const aliases: Record<string, string> = {};
  for (const [from, to] of candidate) {
    if (cyclic.has(from)) continue;
    aliases[from] = to;
    if (!definedIds.has(to) && !rawAliases.has(to)) {
      // The target has no live definition AND is not itself an alias source
      // (a multi-hop chain to a defined terminal is fine).
      advisories.push({ kind: 'dangling', ids: [to] });
    }
  }
  return { aliases, aliasAdvisories: advisories };
}
